// Gemeinsame Helfer für die VM-Abrechnung (/api/billing).

import { customerByNum, customerDisplayName } from '@/helpers/customers';

export const BILLING_COMPONENTS = [
  { key: 'base', label: 'Grundpreis', icon: 'ri-server-line', color: '#405189' },
  { key: 'cpu', label: 'vCPU', icon: 'ri-cpu-line', color: '#299cdb' },
  { key: 'ram', label: 'RAM', icon: 'ri-database-2-line', color: '#0ab39c' },
  { key: 'storage', label: 'Storage', icon: 'ri-hard-drive-2-line', color: '#f7b84b' },
  { key: 'public_ip', label: 'Public IP', icon: 'ri-global-line', color: '#f06548' },
  { key: 'windows_license', label: 'Windows-Lizenz', icon: 'ri-window-2-line', color: '#6559cc' },
];

const componentByKey = new Map(BILLING_COMPONENTS.map((c) => [c.key, c]));

export const billingComponentLabel = (key) => componentByKey.get(key)?.label || key || '-';
export const billingComponentIcon = (key) => componentByKey.get(key)?.icon || 'ri-question-line';
export const billingComponentColor = (key) => componentByKey.get(key)?.color || '#878a99';

export const BILLING_STATUS = {
  billed: {
    label: 'verrechnet',
    badge: 'bg-success-subtle text-success',
    hint: 'Sauber berechnet.',
  },
  unassigned: {
    label: 'kein Kunde',
    badge: 'bg-danger-subtle text-danger',
    hint: 'VM war keinem Kunden zuordenbar (Resource-Pool prüfen).',
  },
  no_plan: {
    label: 'kein Preisplan',
    badge: 'bg-warning-subtle text-warning',
    hint: 'Der Kunde hatte keinen Preisplan – Beträge fehlen.',
  },
  config_error: {
    label: 'Preisplan prüfen',
    badge: 'bg-danger-subtle text-danger',
    hint: 'Preisplan unvollständig oder nur teilweise gültig – Beträge ggf. unvollständig.',
  },
};

export const billingStatusMeta = (status) => BILLING_STATUS[status] || {
  label: status || '-',
  badge: 'bg-secondary-subtle text-secondary',
  hint: '',
};

// Monatsstatus: open = laufender Monat (Month-to-date), final = abgeschlossen.
export const monthStatusMeta = (status) => (status === 'final'
  ? { label: 'abgeschlossen', badge: 'bg-success-subtle text-success' }
  : { label: 'vorläufig', badge: 'bg-warning-subtle text-warning' });

const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export const formatBillingMonth = (billingMonth) => {
  const [y, m] = String(billingMonth || '').split('-');
  const idx = Number(m) - 1;
  if (!y || idx < 0 || idx > 11) return billingMonth || '-';
  return `${MONTH_NAMES[idx]} ${y}`;
};

export const formatBillingMonthShort = (billingMonth) => {
  const [y, m] = String(billingMonth || '').split('-');
  if (!y || !m) return billingMonth || '-';
  return `${m}.${y}`;
};

export const formatChf = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '–';
  return Number(amount).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// UTC-Zeitstempel lokal formatieren (dd.mm.yyyy HH:MM).
export const formatBillingDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const formatBillingDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

// Menge mit Einheit je Kostenkomponente (storage = 10-GB-Blöcke).
export const formatBillingQuantity = (component, quantity) => {
  const q = Number(quantity);
  const qs = Number.isFinite(q) ? q.toLocaleString('de-CH') : String(quantity ?? '-');
  switch (component) {
    case 'base': return `${qs} VM`;
    case 'cpu': return `${qs} ${q === 1 ? 'Core' : 'Cores'}`;
    case 'ram': return `${qs} GB`;
    case 'storage': return `${qs} × 10 GB`;
    case 'public_ip': return `${qs} ${q === 1 ? 'IP' : 'IPs'}`;
    case 'windows_license': return `${qs} ${q === 1 ? 'Lizenz' : 'Lizenzen'}`;
    default: return qs;
  }
};

export const formatBillingDuration = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return '-';
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const parts = [];
  if (days) parts.push(`${days} T.`);
  if (hours) parts.push(`${hours} Std.`);
  if (minutes && !days) parts.push(`${minutes} Min.`);
  if (!parts.length) parts.push(`${Math.max(1, Math.round(s / 60))} Min.`);
  return parts.join(' ');
};

// Anteil des Monats, der von der Berechnung abgedeckt ist (0–100).
const VM_STATUS_LABELS = { running: 'läuft', stopped: 'gestoppt', paused: 'pausiert', suspended: 'angehalten' };
export const vmStatusLabel = (status) => VM_STATUS_LABELS[String(status || '').toLowerCase()] || String(status || '');

export const coveragePercent = (row) => {
  const billed = Number(row?.billedSeconds);
  const month = Number(row?.monthSeconds);
  if (!Number.isFinite(billed) || !Number.isFinite(month) || month <= 0) return null;
  return Math.min(100, Math.round((billed / month) * 1000) / 10);
};

export const customerLabel = (customerNumber) => {
  const n = Number(customerNumber);
  if (!Number.isFinite(n) || n <= 0) return 'nicht zugeordnet';
  // Bezeichnung nur wenn die Admin-Kundenliste geladen ist (Nicht-Admins: nur Nummer).
  const customer = customerByNum(n);
  const name = customer ? customerDisplayName(customer) : '';
  return name ? `K${n} — ${name}` : `K${n}`;
};

// Stabiler Schlüssel einer Abrechnungszeile (IDs ändern sich bei Neuberechnung).
export const billingRowKey = (row) => `${row?.billingMonth}|${row?.clusterName}|${row?.vmid}|${row?.customerNumber}`;

export const billingApiErrorText = (error) => {
  const data = error?.response?.data;
  return data?.error || data?.message || error?.message || 'Unbekannter Fehler.';
};
